import { useSearchParams } from "react-router-dom";
import { useFeed } from "../hooks/useFeed.js";
import LeaderboardCard from "../components/LeaderboardCard.js";
import { FEED_TABS } from "../lib/constants.js";

export default function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "trending";
  const { data, loading, error } = useFeed(activeTab);

  function setActiveTab(tab: string) {
    setSearchParams(tab === "trending" ? {} : { tab });
  }

  return (
    <div>
      {/* Hero */}
      <div className="text-center mb-12">
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-4 tracking-tight">
          <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]">
            What's the vibe?
          </span>
        </h1>
        <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
          The crowd-sourced vibe check for startups.
          <span className="text-slate-300"> Vote in seconds. No sign-up.</span>
        </p>
        {!loading && data.length > 0 && (
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-slate-500 bg-slate-800/40 px-4 py-1.5 rounded-full border border-slate-700/30">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            {data.length} startups tracked
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-2 scrollbar-none">
        {FEED_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`tab ${
              activeTab === tab.value ? "tab-active" : "tab-inactive"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Feed */}
      {loading && (
        <div className="space-y-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="card-hover"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div className="flex items-center gap-4 animate-pulse">
                <div className="w-6 h-6 skeleton rounded-full" />
                <div className="w-12 h-12 skeleton rounded-xl" />
                <div className="flex-1 space-y-2.5">
                  <div className="h-4 skeleton rounded-lg w-1/3" />
                  <div className="h-3 skeleton rounded-lg w-2/3" />
                  <div className="flex gap-2">
                    <div className="h-5 skeleton rounded-full w-16" />
                    <div className="h-5 skeleton rounded-full w-20" />
                  </div>
                </div>
                <div className="w-16 space-y-2">
                  <div className="h-8 skeleton rounded-xl" />
                  <div className="h-3 skeleton rounded w-12" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="card-glow text-center py-10">
          <div className="text-4xl mb-3">😵</div>
          <p className="text-red-400 font-medium mb-1">
            Oops, something broke
          </p>
          <p className="text-slate-500 text-sm mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-secondary"
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !error && data.length === 0 && (
        <div className="card text-center py-16">
          <div className="text-5xl mb-4">🌱</div>
          <p className="text-slate-300 font-medium text-lg mb-1">
            No startups here yet
          </p>
          <p className="text-slate-500 text-sm">
            Be the first to submit one and set the vibe!
          </p>
        </div>
      )}

      {!loading && !error && data.length > 0 && (
        <div className="space-y-3">
          {data.map((startup, i) => (
            <LeaderboardCard
              key={startup.id}
              startup={startup}
              rank={i + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
